import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import {
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  Divider,
  TextField,
  Typography,
} from '@mui/material';
import { axiosClient } from '../api/proxy/AxiosClient';
import { updateUser } from '../redux/authSlice';

const EditProfile = () => {
  const dispatch = useDispatch();
  const authUser = useSelector((state) => state.auth.user);
  const fileInputRef = useRef(null);

  const [form, setForm] = useState({
    firstName: '',
    lastName: '',
    phoneNumber: '',
    email: '',
    portrait: '',
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    async function loadProfile() {
      setLoading(true);
      setError('');
      try {
        const res = await axiosClient.get('/users/me');
        const profile = res.data;
        setForm({
          firstName: profile.firstName || '',
          lastName: profile.lastName || '',
          phoneNumber: profile.phoneNumber || '',
          email: profile.email || '',
          portrait: profile.portrait || '',
        });
      } catch (err) {
        console.error(err);
        setError(err.response?.data?.message || 'Failed to load profile.');
      } finally {
        setLoading(false);
      }
    }

    loadProfile();
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const syncAuthUser = (profile) => {
    dispatch(updateUser({
      firstName: profile.firstName,
      lastName: profile.lastName,
      phoneNumber: profile.phoneNumber,
      portrait: profile.portrait,
    }));
  };

  const handleSave = async () => {
    setSaving(true);
    setError('');
    setSuccess('');
    try {
      const res = await axiosClient.patch('/users/me', {
        firstName: form.firstName,
        lastName: form.lastName,
        phoneNumber: form.phoneNumber || undefined,
      });
      setForm((prev) => ({
        ...prev,
        firstName: res.data.firstName || '',
        lastName: res.data.lastName || '',
        phoneNumber: res.data.phoneNumber || '',
        portrait: res.data.portrait || prev.portrait,
      }));
      syncAuthUser(res.data);
      setSuccess('Profile updated successfully.');
    } catch (err) {
      console.error(err);
      setError(err.response?.data?.message || 'Failed to update profile.');
    } finally {
      setSaving(false);
    }
  };

  const handlePickPhoto = () => {
    if (authUser?.isDemo) {
      setError('Demo users cannot upload a profile photo.');
      return;
    }
    fileInputRef.current?.click();
  };

  const handlePhotoChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) {
      return;
    }

    setUploading(true);
    setError('');
    setSuccess('');

    const formData = new FormData();
    formData.append('file', file);

    try {
      const res = await axiosClient.post('/users/me/userpic', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setForm((prev) => ({ ...prev, portrait: res.data.portrait || '' }));
      syncAuthUser(res.data);
      setSuccess('Profile photo updated.');
    } catch (err) {
      console.error(err);
      setError(err.response?.data?.message || 'Failed to upload profile photo.');
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  if (loading) {
    return (
      <Box p={3}>
        <Typography>Loading profile...</Typography>
      </Box>
    );
  }

  return (
    <Box p={3} display="flex" justifyContent="center">
      <Card sx={{ width: '100%', maxWidth: 480 }}>
        <CardContent>
          <Typography variant="h5" sx={{ fontWeight: 'bold', mb: 2 }}>
            Edit Profile
          </Typography>
          <Divider sx={{ mb: 3 }} />

          <Box display="flex" flexDirection="column" alignItems="center" gap={2} mb={3}>
            <Avatar
              src={form.portrait || undefined}
              sx={{ width: 96, height: 96, fontSize: '2rem' }}
            >
              {form.firstName?.[0] || authUser?.firstName?.[0] || '?'}
            </Avatar>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/jpeg,image/png,image/webp"
              hidden
              onChange={handlePhotoChange}
            />
            <Button
              variant="outlined"
              onClick={handlePickPhoto}
              disabled={authUser?.isDemo || uploading}
            >
              {uploading ? 'Uploading...' : 'Choose Photo'}
            </Button>
          </Box>

          <Box display="flex" flexDirection="column" gap={2}>
            <TextField
              label="First Name"
              name="firstName"
              value={form.firstName}
              onChange={handleChange}
              fullWidth
              disabled={authUser?.isDemo}
            />
            <TextField
              label="Last Name"
              name="lastName"
              value={form.lastName}
              onChange={handleChange}
              fullWidth
              disabled={authUser?.isDemo}
            />
            <TextField
              label="Email"
              name="email"
              value={form.email}
              fullWidth
              slotProps={{ input: { readOnly: true } }}
            />
            <TextField
              label="Phone Number"
              name="phoneNumber"
              value={form.phoneNumber}
              onChange={handleChange}
              fullWidth
              disabled={authUser?.isDemo}
            />
          </Box>

          <Box mt={3} display="flex" flexDirection="column" gap={2}>
            <Button
              variant="contained"
              onClick={handleSave}
              disabled={authUser?.isDemo || saving}
            >
              {saving ? 'Saving...' : 'Save Profile'}
            </Button>
            <Button component={Link} to="/change-password" variant="outlined">
              Change Password
            </Button>
          </Box>

          {error && (
            <Typography color="error" mt={2}>
              {error}
            </Typography>
          )}
          {success && (
            <Typography color="success.main" mt={2}>
              {success}
            </Typography>
          )}
        </CardContent>
      </Card>
    </Box>
  );
};

export default EditProfile;
